import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBack } from '../hooks';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { openInvoicePdf } from '../invoicePdf';

// Every invoice number used, newest first (the director, 15 Oct 2026): the ones
// issued here, with their PDF, and the ones recorded as used outside the app,
// so the sequence has no gap nobody can explain. A new invoice starts from an
// order (Orders → the order → Invoice).

const TEAL = '#14b8a6';
const BORD = '#2a3040';
const CARD = '#1e2329';
const DARK = '#0f1117';
const MUTED = '#6b7280';
const TEXT = '#f0f2f5';
const AMBER = '#f59e0b';
const GREEN = '#4ade80';

const inp = { background: '#161b22', border: `1px solid ${BORD}`, borderRadius: '8px', padding: '7px 9px', fontSize: '13px', color: TEXT, outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'system-ui' };
const lab = { fontSize: '10px', fontWeight: '700', color: MUTED, marginBottom: '4px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em' };
const btn = (primary) => ({
  background: primary ? TEAL : 'transparent', color: primary ? '#0f172a' : TEAL,
  border: `1px solid ${primary ? TEAL : 'rgba(20,184,166,0.6)'}`, borderRadius: '8px', padding: '7px 14px',
  fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'system-ui', whiteSpace: 'nowrap',
});
const group = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
const money = (n) => (n === null || n === undefined ? '—' : `€ ${group(Math.abs(Number(n)).toFixed(2).split('.')[0])},${Number(n).toFixed(2).split('.')[1]}`);
const date = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '—');
const today = () => new Date().toISOString().slice(0, 10);

// Where an invoice stands (the server's payment state, 2 Nov 2026): still in
// the 2% window, due, due today, overdue, or paid -- as one short coloured line.
const RED = '#f87171';
const PAY_COLOUR = { discount: TEAL, open: MUTED, due_today: AMBER, overdue: RED, paid: GREEN };
function PaymentLine({ payment, style }) {
  if (!payment || !payment.says || payment.state === 'paid') return null;
  return <div style={{ fontSize: '11px', fontWeight: 600, color: PAY_COLOUR[payment.state] || MUTED, ...style }}>{payment.says}</div>;
}

export default function Invoices() {
  const goBack = useBack('/admin');
  const navigate = useNavigate();
  const { toast } = useToast();
  const [list, setList] = useState(null);
  const [next, setNext] = useState(null);
  const [outside, setOutside] = useState(null);
  const [paying, setPaying] = useState(null);   // { id, date }

  const load = () => api.get('/api/v1/invoices').then(r => { setList(r.data.invoices || []); setNext(r.data.next_number); })
    .catch(err => toast.error(err.response?.data?.error || 'The invoices could not be read just now.'));
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Every order, as a card (the director, 15 Oct 2026): the one completed last
  // on top, then the ones coming, in date order; older completed ones folded
  // away below. Each says who, which week, when it goes, how much of it is
  // done, and its invoice -- to make, or to open.
  const [orders, setOrders] = useState(null);
  const [showEarlier, setShowEarlier] = useState(false);
  useEffect(() => {
    api.get('/api/v1/orders?status=all').then(r => setOrders(Array.isArray(r.data) ? r.data : []))
      .catch(err => toast.error(err.response?.data?.error || 'The orders could not be read just now.'));
  }, []); // eslint-disable-line

  const openPdf = (inv) => openInvoicePdf(inv, toast);

  // The invoice or the delivery note on its own, one copy, saved as a file
  // named as he names them: "00020-2026 Invoice - Sera (16-10-2026).pdf".
  async function downloadPart(inv, part) {
    const what = part === 'invoice' ? 'Invoice' : 'Delivery Note';
    try {
      const r = await api.get(`/api/v1/invoices/${inv.id}/pdf?part=${part}`, { responseType: 'blob' });
      const who = ((inv.customer_snapshot && inv.customer_snapshot.name) || 'Customer').split(' ')[0];
      const d = inv.dispatch_date ? inv.dispatch_date.split('-').reverse().join('-') : '';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      a.download = `${inv.number} ${what} - ${who}${d ? ` (${d})` : ''}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (err) {
      let msg = `The ${what.toLowerCase()} of ${inv.number} could not be made just now.`;
      try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch { /* keep */ }
      toast.error(msg);
    }
  }

  async function saveOutside() {
    try {
      await api.post('/api/v1/invoices/outside', outside);
      toast.success(`${outside.number} recorded`);
      setOutside(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not record it.');
    }
  }

  async function savePaid() {
    try {
      await api.put(`/api/v1/invoices/${paying.id}/paid`, { paid_at: paying.date || null });
      setPaying(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save it.');
    }
  }

  return (
    <div style={{ padding: '20px', fontFamily: 'system-ui, sans-serif', color: TEXT, background: DARK, minHeight: '100vh', boxSizing: 'border-box' }}>
      <button onClick={goBack} style={{ background: 'none', border: 'none', color: MUTED, fontSize: '12px', cursor: 'pointer', padding: 0, marginBottom: '8px' }}>
        <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px', marginRight: '4px' }} />Back
      </button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '700', margin: '0 0 4px' }}>Invoices</h1>
          <p style={{ color: MUTED, fontSize: '13px', margin: 0 }}>
            Next number: <b style={{ color: TEAL }}>{next ? `#${next}` : '…'}</b> · an invoice starts from its order below
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={btn(false)} onClick={() => navigate('/admin/orders')}><i className="fa-solid fa-boxes-stacked" style={{ fontSize: '11px', marginRight: '5px' }} />Orders</button>
          <button style={btn(false)} onClick={() => navigate('/admin/invoices/weights')}><i className="fa-solid fa-weight-hanging" style={{ fontSize: '11px', marginRight: '5px' }} />Weight calculator</button>
          <button style={btn(false)} onClick={() => setOutside({ number: next || '', date: today(), to: '', total: '', note: '' })}>
            <i className="fa-solid fa-plus" style={{ fontSize: '11px', marginRight: '5px' }} />Number used outside the app
          </button>
        </div>
      </div>

      {outside && (
        <div style={{ background: CARD, border: `1px solid ${TEAL}`, borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '4px' }}>Record a number used outside the app</div>
          <div style={{ fontSize: '12px', color: MUTED, marginBottom: '12px' }}>For an invoice made elsewhere — a service, say. It takes its place in the sequence, and the app's next number is the one after it.</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
            <div><span style={lab}>Number</span><input style={inp} value={outside.number} onChange={e => setOutside(o => ({ ...o, number: e.target.value }))} /></div>
            <div><span style={lab}>Date</span><input type="date" style={inp} value={outside.date} onChange={e => setOutside(o => ({ ...o, date: e.target.value }))} /></div>
            <div><span style={lab}>To</span><input style={inp} value={outside.to} onChange={e => setOutside(o => ({ ...o, to: e.target.value }))} placeholder="Who it was to" /></div>
            <div><span style={lab}>Amount € (optional)</span><input style={inp} value={outside.total} onChange={e => setOutside(o => ({ ...o, total: e.target.value }))} /></div>
            <div style={{ gridColumn: '1 / -1' }}><span style={lab}>Note (optional)</span><input style={inp} value={outside.note} onChange={e => setOutside(o => ({ ...o, note: e.target.value }))} placeholder="What it was for" /></div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
            <button style={{ ...btn(false), color: '#9ca3af', borderColor: '#374151' }} onClick={() => setOutside(null)}>Cancel</button>
            <button style={btn(true)} onClick={saveOutside}>Record it</button>
          </div>
        </div>
      )}

      {orders && (() => {
        const when = (o) => o.dispatch_date || o.delivery_date || o.suggested_dispatch_date || '';
        const done = orders.filter(o => o.status === 'fulfilled').sort((a, b) => String(b.fulfilled_at || when(b)).localeCompare(String(a.fulfilled_at || when(a))));
        const open = orders.filter(o => o.status !== 'fulfilled').sort((a, b) => when(a).localeCompare(when(b)));
        const main = [...done.slice(0, 1), ...open];
        const earlier = done.slice(1);
        const card = (o) => <OrderCard key={o.id} order={o} invoices={(list || []).filter(i => i.order_id === o.id && i.source !== 'outside')}
          onCreate={() => navigate(`/admin/invoices/new?order=${o.id}`)} onOpen={openPdf} onDownload={downloadPart} onShowOrder={() => navigate(`/admin/orders?order=${o.id}`)} />;
        return (
          <div style={{ marginBottom: '22px' }}>
            <h2 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 10px' }}>Orders</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {main.map(card)}
              {!main.length && <div style={{ color: MUTED, fontSize: '13px' }}>No orders.</div>}
            </div>
            {earlier.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <button onClick={() => setShowEarlier(v => !v)} style={{ background: 'none', border: 'none', color: TEAL, fontSize: '13px', fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                  <i className={`fa-solid fa-chevron-${showEarlier ? 'down' : 'right'}`} style={{ fontSize: '10px', marginRight: '6px' }} />
                  Earlier orders · {earlier.length}
                </button>
                {showEarlier && <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>{earlier.map(card)}</div>}
              </div>
            )}
          </div>
        );
      })()}

      <h2 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 10px' }}>Invoice numbers</h2>
      <div style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: '12px', padding: '8px 16px', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '760px' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: MUTED, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              <th style={{ padding: '8px 6px' }}>Number</th><th style={{ padding: '8px 6px' }}>To</th><th style={{ padding: '8px 6px' }}>Order</th>
              <th style={{ padding: '8px 6px' }}>Dispatch</th><th style={{ padding: '8px 6px' }}>Due</th>
              <th style={{ padding: '8px 6px', textAlign: 'right' }}>Total</th><th style={{ padding: '8px 6px' }}>Paid</th><th style={{ padding: '8px 6px' }} />
            </tr>
          </thead>
          <tbody>
            {(list || []).map(inv => {
              const pay = inv.payment;
              const overdue = pay ? pay.state === 'overdue' : (!inv.paid_at && inv.due_date && inv.due_date < today());
              return (
                <tr key={inv.id} style={{ borderTop: `1px solid ${BORD}` }}>
                  <td style={{ padding: '8px 6px', fontWeight: 700, color: TEAL }}>#{inv.number}</td>
                  <td style={{ padding: '8px 6px' }}>
                    {inv.customer_snapshot?.name || '—'}
                    {inv.source === 'outside' && <span style={{ fontSize: '10px', color: MUTED, border: `1px solid ${BORD}`, borderRadius: '4px', padding: '0 5px', marginLeft: '6px' }}>outside the app</span>}
                    {inv.note && <div style={{ fontSize: '11px', color: MUTED }}>{inv.note}</div>}
                  </td>
                  <td style={{ padding: '8px 6px', color: '#d1d5db', whiteSpace: 'pre-line', fontSize: '12px' }}>{inv.order_reference || '—'}</td>
                  <td style={{ padding: '8px 6px' }}>{date(inv.dispatch_date)}</td>
                  <td style={{ padding: '8px 6px', color: overdue ? RED : TEXT }}>
                    {date(inv.due_date)}
                    {pay ? <PaymentLine payment={pay} style={{ marginTop: '2px' }} /> : (overdue ? ' · overdue' : '')}
                  </td>
                  <td style={{ padding: '8px 6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{money(inv.total)}</td>
                  <td style={{ padding: '8px 6px' }}>
                    {paying && paying.id === inv.id ? (
                      <span style={{ display: 'inline-flex', gap: '6px' }}>
                        <input type="date" style={{ ...inp, width: '140px' }} value={paying.date} onChange={e => setPaying(p => ({ ...p, date: e.target.value }))} />
                        <button style={btn(true)} onClick={savePaid}>Save</button>
                      </span>
                    ) : inv.paid_at
                      ? <span>
                          <button onClick={() => setPaying({ id: inv.id, date: inv.paid_at })} style={{ background: 'none', border: 'none', color: GREEN, cursor: 'pointer', padding: 0, fontSize: '13px' }}>✓ {date(inv.paid_at)}</button>
                          {pay && pay.state === 'paid' && (pay.within_discount || pay.late_days > 0) && (
                            <div style={{ fontSize: '11px', color: pay.within_discount ? TEAL : MUTED }}>{pay.within_discount ? 'within the 2% window' : `${pay.late_days} day${pay.late_days === 1 ? '' : 's'} late`}</div>
                          )}
                        </span>
                      : <button onClick={() => setPaying({ id: inv.id, date: today() })} style={{ ...btn(false), padding: '3px 9px', fontSize: '11px' }}>Mark paid</button>}
                  </td>
                  <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                    {inv.source !== 'outside' && (
                      <span style={{ display: 'inline-flex', gap: '5px' }}>
                        <button onClick={() => openPdf(inv)} title="The invoice and its two delivery notes" style={{ ...btn(false), padding: '3px 9px', fontSize: '11px' }}><i className="fa-solid fa-file-pdf" style={{ marginRight: '4px' }} />PDF</button>
                        <button onClick={() => downloadPart(inv, 'invoice')} title="Download the invoice alone, one copy" style={{ ...btn(false), padding: '3px 9px', fontSize: '11px' }}><i className="fa-solid fa-download" style={{ marginRight: '4px' }} />Invoice</button>
                        <button onClick={() => downloadPart(inv, 'delivery_note')} title="Download the delivery note alone, one copy" style={{ ...btn(false), padding: '3px 9px', fontSize: '11px' }}><i className="fa-solid fa-download" style={{ marginRight: '4px' }} />Delivery note</button>
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {list && !list.length && (
              <tr><td colSpan={8} style={{ padding: '20px 6px', color: MUTED, textAlign: 'center' }}>
                No invoices yet. The first from the app will be #{next}: open an order and press Invoice.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// One order as a card. A line is done when what was made and what came from
// stock cover it (as on Orders); a product is done when all its lines are.
const longDate = (iso) => (iso ? new Date(iso.slice(0, 10) + 'T12:00:00Z').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '');
function OrderCard({ order, invoices, onCreate, onOpen, onDownload, onShowOrder }) {
  const lines = order.lines || [];
  const lineDone = (l) => (l.quantity || 0) > 0 && (Number(l.current_qty) || 0) + (Number(l.from_stock_qty) || 0) >= l.quantity;
  const items = [];
  const byProduct = {};
  for (const l of lines) {
    if (l.product_id) {
      if (!byProduct[l.product_id]) { byProduct[l.product_id] = { done: true }; items.push(byProduct[l.product_id]); }
      byProduct[l.product_id].done = byProduct[l.product_id].done && lineDone(l);
    } else items.push({ done: lineDone(l) });
  }
  const doneCount = items.filter(i => i.done).length;
  const fulfilled = order.status === 'fulfilled';
  const dispatch = order.dispatch_date
    ? { text: `Dispatch ${longDate(order.dispatch_date)}`, sure: true }
    : order.suggested_dispatch_date ? { text: `Dispatch ${longDate(order.suggested_dispatch_date)} (not confirmed)`, sure: false } : { text: 'No dispatch date yet', sure: false };
  const pct = items.length ? Math.round(doneCount / items.length * 100) : 0;
  // The small download buttons under "Show invoice".
  const small = { ...btn(false), flex: 1, padding: '3px 8px', fontSize: '11px', borderRadius: '6px' };
  const ghost = { background: 'transparent', color: '#6b7280', border: '1px solid #374151', borderRadius: '8px', padding: '7px 14px', fontSize: '13px', fontWeight: 700, cursor: 'not-allowed', whiteSpace: 'nowrap', fontFamily: 'system-ui' };
  return (
    <div style={{ background: CARD, border: `1px solid ${fulfilled ? 'rgba(74,222,128,0.35)' : BORD}`, borderRadius: '12px', padding: '14px 16px',
      display: 'flex', gap: '16px', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <div style={{ minWidth: '220px', flex: '1 1 220px' }}>
        <div style={{ fontSize: '12px', color: MUTED }}>{order.customer || '—'}</div>
        <div style={{ fontSize: '17px', fontWeight: 800 }}>
          <button onClick={onShowOrder} title="Open this order"
            style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'rgba(240,242,245,0.25)', textUnderlineOffset: '4px' }}>
            {order.order_number || `Order ${order.id}`}
            <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: '10px', color: MUTED, marginLeft: '6px', verticalAlign: 'middle' }} />
          </button>
          {order.delivery_week && <span style={{ fontSize: '12px', color: MUTED, fontWeight: 500, marginLeft: '8px' }}>Wk {order.delivery_week}</span>}
          {fulfilled && <span style={{ fontSize: '10.5px', color: GREEN, border: '1px solid rgba(74,222,128,0.5)', borderRadius: '4px', padding: '1px 6px', marginLeft: '8px', fontWeight: 700 }}>Completed{order.fulfilled_at ? ` ${date(order.fulfilled_at.slice(0, 10))}` : ''}</span>}
        </div>
        <div style={{ fontSize: '12.5px', color: dispatch.sure ? TEXT : MUTED, marginTop: '2px' }}>{dispatch.text}</div>
      </div>
      <div style={{ minWidth: '170px', flex: '0 1 220px' }}>
        <div style={{ fontSize: '12.5px', color: TEXT }}>
          <b>{items.length}</b> {items.length === 1 ? 'product / part' : 'products / parts'} · <b style={{ color: doneCount === items.length && items.length ? GREEN : TEXT }}>{doneCount}</b> completed
        </div>
        <div style={{ height: '6px', background: '#161b22', borderRadius: '3px', marginTop: '6px', overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? GREEN : TEAL }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {invoices.length
          ? invoices.map(inv => (
            <div key={inv.id} style={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'stretch' }}>
              <button style={btn(false)} onClick={() => onOpen(inv)} title="Open the invoice and its two delivery notes">
                <i className="fa-solid fa-file-pdf" style={{ fontSize: '11px', marginRight: '6px' }} />Show invoice #{inv.number}
              </button>
              <PaymentLine payment={inv.payment} style={{ textAlign: 'center' }} />
              <div style={{ display: 'flex', gap: '5px' }}>
                <button style={small} onClick={() => onDownload(inv, 'invoice')} title="The invoice alone, one copy, as a PDF file">
                  <i className="fa-solid fa-download" style={{ fontSize: '9px', marginRight: '4px' }} />Invoice
                </button>
                <button style={small} onClick={() => onDownload(inv, 'delivery_note')} title="The delivery note alone, one copy, as a PDF file">
                  <i className="fa-solid fa-download" style={{ fontSize: '9px', marginRight: '4px' }} />Delivery note
                </button>
              </div>
            </div>))
          : (
            <button style={btn(true)} onClick={onCreate} title="The invoice and two copies of the delivery note">
              <i className="fa-solid fa-file-invoice" style={{ fontSize: '11px', marginRight: '6px' }} />Create an invoice
            </button>)}
        <button style={ghost} disabled title="Waiting for the credit note template">
          <i className="fa-solid fa-file-circle-minus" style={{ fontSize: '11px', marginRight: '6px' }} />Issue a credit note
        </button>
      </div>
    </div>
  );
}
